import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Eye, 
  User,
  UserX, 
  UserCheck,
  Phone,
  Mail,
  Calendar,
  MapPin,
  AlertCircle,
  ShoppingBag,
  CreditCard,
  Upload,
  Download,
  Package,
  DollarSign,
  Send,
  Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Members = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [members, setMembers] = useState([]);
  const [demographics, setDemographics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEditSubscriptionModal, setShowEditSubscriptionModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    birth_date: '',
    postal_code: '',
    gender: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: '',
    photo_url: ''
  });
  const [geoInfo, setGeoInfo] = useState(null);   // { city, province, region }
  const [geoLoading, setGeoLoading] = useState(false);
  const geoTimerRef = useRef(null);

  const resolvePostalCode = useCallback(async (cp) => {
    if (!cp || cp.length !== 5 || !/^\d{5}$/.test(cp)) {
      setGeoInfo(null);
      return;
    }
    setGeoLoading(true);
    try {
      const res = await axios.get(`${API}/postal-code/${cp}`);
      setGeoInfo(res.data);
    } catch {
      setGeoInfo({ error: 'Código postal no encontrado' });
    } finally {
      setGeoLoading(false);
    }
  }, []);

  const handlePostalCodeChange = (value) => {
    const clean = value.replace(/\D/g, '').slice(0, 5);
    setFormData({ ...formData, postal_code: clean });
    if (geoTimerRef.current) clearTimeout(geoTimerRef.current);
    geoTimerRef.current = setTimeout(() => resolvePostalCode(clean), 400);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  const getStatusBadge = (member) => {
    if (member?.status === 'no_socio') {
      return <Badge className="bg-zinc-700/40 text-zinc-300 border border-zinc-600" data-testid="badge-no-socio">No socio</Badge>;
    }
    if (member?.is_active) {
      return <Badge className="bg-emerald-600/20 text-emerald-400" data-testid="badge-active">Activo</Badge>;
    }
    return <Badge className="bg-amber-600/20 text-amber-400" data-testid="badge-inactive">Inactivo</Badge>;
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (genderFilter) params.append('gender', genderFilter);
      
      const response = await axios.get(`${API}/members?${params.toString()}`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Error al cargar los socios');
    } finally {
      setLoading(false);
    }
  };

  const fetchDemographics = async () => {
    try {
      const res = await axios.get(`${API}/members/demographics`);
      setDemographics(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, genderFilter]);

  useEffect(() => {
    fetchDemographics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMembers();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleCreate = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Nombre y apellido y email son obligatorios');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('El email no es válido');
      return;
    }
    
    try {
      await axios.post(`${API}/members`, formData);
      toast.success('Socio creado correctamente');
      setShowCreateModal(false);
      resetForm();
      fetchMembers();
      fetchDemographics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el socio');
    }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Nombre y apellido y email son obligatorios');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('El email no es válido');
      return;
    }
    
    try {
      await axios.put(`${API}/members/${selectedMember.id}`, formData);
      toast.success('Socio actualizado correctamente');
      setShowEditModal(false);
      resetForm();
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el socio');
    }
  };

  const handleDeactivate = async (memberId) => {
    try {
      await axios.delete(`${API}/members/${memberId}`);
      toast.success('Socio desactivado');
      fetchMembers();
    } catch (error) {
      toast.error('Error al desactivar el socio');
    }
  };

  const handleReactivate = async (memberId) => {
    try {
      await axios.post(`${API}/members/${memberId}/reactivate`);
      toast.success('Socio reactivado');
      fetchMembers();
    } catch (error) {
      toast.error('Error al reactivar el socio');
    }
  };

  const handleImportFromSales = async () => {
    try {
      const response = await axios.post(`${API}/members/import-from-sales`);
      toast.success(`Importación completada: ${response.data.imported} socios importados, ${response.data.skipped} ya existían`);
      fetchMembers();
    } catch (error) {
      toast.error('Error al importar socios');
    }
  };

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleExcelFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so selecting the same file again fires onChange
    e.target.value = '';
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xlsm')) {
      toast.error('El archivo debe ser .xlsx');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    setImporting(true);
    setImportResult(null);
    try {
      const res = await axios.post(`${API}/members/import-from-excel`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,   // 5 min for large files (557 rows ~ 1-2 min with CP lookups)
      });
      setImportResult(res.data);
      const created = res.data.imported ?? 0;
      const upd = res.data.updated ?? 0;
      const sk = res.data.skipped ?? 0;
      toast.success(`✓ ${created} creados · ${upd} actualizados · ${sk} omitidos`);
      fetchMembers();
      fetchDemographics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el Excel');
    } finally {
      setImporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/members/export-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `socios_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('No se pudo exportar el Excel');
    }
  };

  const handleBackfillSalesLinks = async () => {
    if (!window.confirm('Esto vinculará las ventas y deudas antiguas con los socios registrados usando el teléfono.\n\n¿Continuar?')) return;
    try {
      const res = await axios.post(`${API}/members/backfill-sales-links`);
      toast.success(`✓ ${res.data.sales_updated} ventas y ${res.data.debts_updated} deudas vinculadas`);
      fetchMembers();
      fetchDemographics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vincular ventas antiguas');
    }
  };

  const handleInvitePortal = async (member) => {
    if (!member.email) {
      toast.error('Añade un email al socio antes de enviarle la invitación.');
      return;
    }
    try {
      const res = await axios.post(`${API}/members/${member.id}/invite-portal`);
      if (res.data.email_sent) {
        toast.success(`Invitación enviada a ${res.data.email}`);
      } else {
        toast.warning('Invitación generada pero el email no se envió. Revisa la configuración de Resend.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la invitación');
    }
  };

  const handleConsumeCredit = async (sub, count) => {
    try {
      await axios.post(`${API}/sales/${sub.sale_id}/subscription-consume`, {
        item_index: sub.item_index,
        count,
      });
      toast.success(count > 0 ? 'Crédito registrado' : 'Crédito restaurado');
      await refreshSelectedMember();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar créditos');
    }
  };

  const handlePermanentDelete = async (member) => {
    const name = member?.name || 'este socio';
    const confirmText = prompt(
      `⚠ ESTA ACCIÓN ES IRREVERSIBLE.\n\n` +
      `Vas a eliminar PERMANENTEMENTE a ${name}.\n` +
      `Sus compras y deudas seguirán en el sistema pero el socio desaparecerá.\n\n` +
      `Para confirmar, escribe el NOMBRE EXACTO del socio:`
    );
    if (!confirmText || confirmText.trim() !== name.trim()) {
      if (confirmText !== null) toast.error('El nombre no coincide. Eliminación cancelada.');
      return;
    }
    try {
      await axios.delete(`${API}/members/${member.id}/permanent`);
      toast.success(`Socio eliminado: ${name}`);
      setShowDetailModal(false);
      setSelectedMember(null);
      fetchMembers();
      fetchDemographics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar socio');
    }
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setFormData({
      name: member.name || '',
      phone: member.phone || '',
      email: member.email || '',
      birth_date: member.birth_date || '',
      postal_code: member.postal_code || '',
      gender: member.gender || '',
      emergency_contact: member.emergency_contact || '',
      emergency_phone: member.emergency_phone || '',
      notes: member.notes || '',
      photo_url: member.photo_url || ''
    });
    // Show pre-existing city/province when editing
    if (member.postal_code && member.city) {
      setGeoInfo({ city: member.city, province: member.province, region: member.region });
    } else {
      setGeoInfo(null);
    }
    setShowEditModal(true);
  };

  const openDetailModal = async (member) => {
    try {
      const response = await axios.get(`${API}/members/${member.id}`);
      setSelectedMember(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Error al cargar los detalles del socio');
    }
  };

  const refreshSelectedMember = async () => {
    if (!selectedMember?.id) return;
    try {
      const response = await axios.get(`${API}/members/${selectedMember.id}`);
      setSelectedMember(response.data);
    } catch (error) {
      // silent
    }
  };

  const openEditSubscriptionModal = (subscription) => {
    setSelectedSubscription(subscription);
    setNewExpiryDate(subscription.subscription_end || '');
    setShowEditSubscriptionModal(true);
  };

  const handleUpdateSubscriptionEnd = async () => {
    if (!newExpiryDate) {
      toast.error('Selecciona una fecha válida');
      return;
    }
    try {
      await axios.put(`${API}/sales/${selectedSubscription.sale_id}/subscription-end`, {
        subscription_end: newExpiryDate,
        item_index: selectedSubscription.item_index
      });
      toast.success('Fecha de caducidad actualizada');
      setShowEditSubscriptionModal(false);
      setSelectedSubscription(null);
      setNewExpiryDate('');
      await refreshSelectedMember();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la fecha');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      birth_date: '',
      postal_code: '',
      gender: '',
      emergency_contact: '',
      emergency_phone: '',
      notes: '',
      photo_url: ''
    });
    setGeoInfo(null);
    setSelectedMember(null);
  };

  const renderMemberForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-zinc-400">Nombre y Apellido *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="Nombre y apellido"
            className="bg-zinc-900 border-zinc-700"
            data-testid="member-form-name"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Teléfono</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            placeholder="600 123 456 (opcional)"
            className="bg-zinc-900 border-zinc-700"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-zinc-400">Email *</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="email@ejemplo.com"
            className="bg-zinc-900 border-zinc-700"
            data-testid="member-form-email"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Fecha de nacimiento</Label>
          <Input
            type="date"
            value={formData.birth_date}
            onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
            className="bg-zinc-900 border-zinc-700"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-zinc-400">Género</Label>
          <select
            value={formData.gender}
            onChange={(e) => setFormData({...formData, gender: e.target.value})}
            className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-700 px-3 text-sm text-white"
            data-testid="member-form-gender"
          >
            <option value="">-</option>
            <option value="Masculino">Masculino</option>
            <option value="Femenino">Femenino</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Código postal</Label>
          <Input
            value={formData.postal_code}
            onChange={(e) => handlePostalCodeChange(e.target.value)}
            placeholder="28001"
            maxLength={5}
            inputMode="numeric"
            className="bg-zinc-900 border-zinc-700 font-mono"
            data-testid="member-form-postal-code"
          />
        </div>
      </div>
      {formData.postal_code && formData.postal_code.length === 5 && (
        <div className="-mt-2 text-xs" data-testid="postal-code-geo">
          {geoLoading ? (
            <span className="text-zinc-500">Buscando localidad…</span>
          ) : geoInfo?.error ? (
            <span className="text-amber-400">⚠ {geoInfo.error}</span>
          ) : geoInfo?.city ? (
            <span className="text-emerald-400">
              📍 {geoInfo.city}{geoInfo.province ? `, ${geoInfo.province}` : ''}{geoInfo.region && geoInfo.region !== geoInfo.province ? ` (${geoInfo.region})` : ''}
            </span>
          ) : null}
        </div>
      )}
      
      {/* Emergency Contact */}
      <div className="border-t border-zinc-800 pt-3">
        <p className="text-xs text-zinc-500 mb-2">Contacto de emergencia</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Nombre</Label>
            <Input
              value={formData.emergency_contact}
              onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
              placeholder="Nombre del contacto"
              className="bg-zinc-900 border-zinc-700"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Teléfono</Label>
            <Input
              value={formData.emergency_phone}
              onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
              placeholder="600 000 000"
              className="bg-zinc-900 border-zinc-700"
            />
          </div>
        </div>
      </div>
      
      {/* Notes */}
      <div>
        <Label className="text-xs text-zinc-400">Notas / Observaciones</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Notas adicionales..."
          className="bg-zinc-900 border-zinc-700 h-20"
        />
      </div>
      
      <DialogFooter>
        <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }} className="border-zinc-700">
          Cancelar
        </Button>
        <Button onClick={onSubmit} className="bg-emerald-600 hover:bg-emerald-700">
          {submitLabel}
        </Button>
      </DialogFooter>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="text-emerald-400" />
            Gestión de Socios
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{members.length} socios registrados</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={handleExcelFileSelected}
                className="hidden"
                data-testid="import-excel-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="border-zinc-700 text-zinc-300"
                data-testid="import-excel-btn"
              >
                <Upload size={16} className="mr-2" />
                {importing ? 'Importando…' : 'Importar desde Excel'}
              </Button>
              <Button
                variant="outline"
                onClick={handleExportExcel}
                className="border-zinc-700 text-zinc-300"
                data-testid="export-excel-btn"
              >
                <Download size={16} className="mr-2" />
                Exportar a Excel
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={handleBackfillSalesLinks}
                  className="border-amber-700/40 text-amber-300 hover:bg-amber-600/10"
                  data-testid="backfill-sales-links-btn"
                  title="Vincula las ventas antiguas (anteriores a la actualización) a los socios registrados usando el teléfono"
                >
                  Vincular ventas antiguas
                </Button>
              )}
            </>
          )}
          <Button 
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus size={16} className="mr-2" />
            Nuevo Socio
          </Button>
        </div>
      </div>

      {/* Import from Excel result banner */}
      {importResult && (
        <div className="bg-emerald-900/10 border border-emerald-800/50 rounded p-3 mb-4 flex justify-between items-start gap-2" data-testid="import-result-banner">
          <div className="text-sm flex-1">
            <p className="text-emerald-300 font-medium">Importación Excel finalizada</p>
            <p className="text-zinc-400 text-xs mt-1">
              ✓ <b className="text-white">{importResult.imported}</b> socios creados · 
              ⏭ <b className="text-zinc-300">{importResult.skipped}</b> omitidos (teléfono ya existente o datos incompletos)
              {importResult.total_errors > 0 && (
                <> · ⚠ <b className="text-amber-400">{importResult.total_errors}</b> filas con errores</>
              )}
            </p>
            {importResult.errors && importResult.errors.length > 0 && (
              <details className="text-[11px] text-zinc-500 mt-2">
                <summary className="cursor-pointer text-zinc-400">Ver errores ({importResult.errors.length})</summary>
                <ul className="mt-1 ml-3 list-disc">
                  {importResult.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>Fila {e.row}: {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <button onClick={() => setImportResult(null)} className="text-zinc-500 hover:text-white text-xs" data-testid="dismiss-import-result">✕</button>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#0A0A0A] border-zinc-800 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <Input
                placeholder="Buscar por nombre, teléfono o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-700"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={statusFilter === '' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('')}
                className={statusFilter === '' ? 'bg-emerald-600' : 'border-zinc-700'}
                size="sm"
                data-testid="filter-all"
              >
                Todos
              </Button>
              <Button
                variant={statusFilter === 'active' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('active')}
                className={statusFilter === 'active' ? 'bg-emerald-600' : 'border-zinc-700'}
                size="sm"
                data-testid="filter-active"
              >
                Activos
              </Button>
              <Button
                variant={statusFilter === 'inactive' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('inactive')}
                className={statusFilter === 'inactive' ? 'bg-amber-600' : 'border-zinc-700'}
                size="sm"
                data-testid="filter-inactive"
              >
                Inactivos
              </Button>
              <Button
                variant={statusFilter === 'no_socio' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('no_socio')}
                className={statusFilter === 'no_socio' ? 'bg-zinc-600' : 'border-zinc-700'}
                size="sm"
                data-testid="filter-no-socio"
              >
                No socios
              </Button>
              <div className="w-px h-6 bg-zinc-800 mx-1 self-center" />
              <Button
                variant={genderFilter === '' ? 'default' : 'outline'}
                onClick={() => setGenderFilter('')}
                className={genderFilter === '' ? 'bg-zinc-700' : 'border-zinc-700'}
                size="sm"
                data-testid="filter-gender-all"
              >
                Ambos
              </Button>
              <Button
                variant={genderFilter === 'Masculino' ? 'default' : 'outline'}
                onClick={() => setGenderFilter('Masculino')}
                className={genderFilter === 'Masculino' ? 'bg-blue-600' : 'border-zinc-700'}
                size="sm"
                data-testid="filter-gender-masculino"
              >
                ♂ Masculino
              </Button>
              <Button
                variant={genderFilter === 'Femenino' ? 'default' : 'outline'}
                onClick={() => setGenderFilter('Femenino')}
                className={genderFilter === 'Femenino' ? 'bg-pink-600' : 'border-zinc-700'}
                size="sm"
                data-testid="filter-gender-femenino"
              >
                ♀ Femenino
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demographics breakdown card */}
      {demographics && demographics.total_socios > 0 && (
        <Card className="bg-[#0A0A0A] border-zinc-800 mb-6" data-testid="demographics-card">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Total socios</p>
                <p className="text-2xl font-bold text-white mt-1">{demographics.total_socios}</p>
                <p className="text-xs text-zinc-500">{demographics.no_socios} no socios</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Activos</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">{demographics.active_socios}</p>
                <p className="text-xs text-zinc-500">{demographics.total_socios > 0 ? Math.round(demographics.active_socios / demographics.total_socios * 100) : 0}% del total</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Inactivos</p>
                <p className="text-2xl font-bold text-amber-400 mt-1">{demographics.inactive_socios}</p>
                <p className="text-xs text-zinc-500">sin suscripción vigente</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">♂ Masculino</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">{demographics.by_gender.masculino}</p>
                <p className="text-xs text-zinc-500">
                  {demographics.total_socios > 0 ? Math.round(demographics.by_gender.masculino / demographics.total_socios * 100) : 0}%
                  {demographics.active_by_gender.masculino > 0 && ` · ${demographics.active_by_gender.masculino} activos`}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">♀ Femenino</p>
                <p className="text-2xl font-bold text-pink-400 mt-1">{demographics.by_gender.femenino}</p>
                <p className="text-xs text-zinc-500">
                  {demographics.total_socios > 0 ? Math.round(demographics.by_gender.femenino / demographics.total_socios * 100) : 0}%
                  {demographics.active_by_gender.femenino > 0 && ` · ${demographics.active_by_gender.femenino} activas`}
                </p>
              </div>
            </div>
            {/* Visual bar */}
            {(demographics.by_gender.masculino + demographics.by_gender.femenino) > 0 && (
              <div className="mt-3 h-1.5 w-full rounded-full overflow-hidden bg-zinc-800 flex">
                <div
                  className="bg-blue-500"
                  style={{ width: `${demographics.by_gender.masculino / (demographics.by_gender.masculino + demographics.by_gender.femenino) * 100}%` }}
                  title={`${demographics.by_gender.masculino} masculino`}
                />
                <div
                  className="bg-pink-500 flex-1"
                  title={`${demographics.by_gender.femenino} femenino`}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Members Table */}
      <Card className="bg-[#0A0A0A] border-zinc-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-zinc-500">Cargando socios...</div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              <Users size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay socios registrados</p>
              <p className="text-xs mt-1">Crea el primer socio o importa del historial de ventas</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500 w-20">Nº</TableHead>
                  <TableHead className="text-zinc-500">Nombre y Apellido</TableHead>
                  <TableHead className="text-zinc-500">Teléfono</TableHead>
                  <TableHead className="text-zinc-500 hidden sm:table-cell">Email</TableHead>
                  <TableHead className="text-zinc-500 text-right hidden sm:table-cell">Deuda</TableHead>
                  <TableHead className="text-zinc-500 text-center">Estado</TableHead>
                  <TableHead className="text-zinc-500 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id} className="border-zinc-800">
                    <TableCell className="font-mono text-emerald-400 text-xs" data-testid={`member-number-${member.member_number}`}>
                      {member.member_number || '—'}
                    </TableCell>
                    <TableCell className="font-medium text-white">{member.name}</TableCell>
                    <TableCell className="text-zinc-400">
                      <div className="flex items-center gap-1">
                        <Phone size={12} />
                        {member.phone}
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400 hidden sm:table-cell">
                      {member.email || '-'}
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell">
                      {member.pending_debt > 0 ? (
                        <span className="text-red-400 font-mono">{formatCurrency(member.pending_debt)}</span>
                      ) : (
                        <span className="text-zinc-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {getStatusBadge(member)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetailModal(member)}
                          className="text-zinc-400 hover:text-white"
                          data-testid={`view-member-${member.member_number}`}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(member)}
                          className="text-zinc-400 hover:text-white"
                          data-testid={`edit-member-${member.member_number}`}
                        >
                          <Edit size={14} />
                        </Button>
                        {isAdmin && (
                          member.status !== 'no_socio' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeactivate(member.id)}
                              className="text-zinc-400 hover:text-red-400"
                              title="Marcar como NO SOCIO"
                              data-testid={`mark-no-socio-${member.member_number}`}
                            >
                              <UserX size={14} />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReactivate(member.id)}
                              className="text-zinc-400 hover:text-emerald-400"
                              title="Restaurar como socio"
                              data-testid={`restore-socio-${member.member_number}`}
                            >
                              <UserCheck size={14} />
                            </Button>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="text-emerald-400" size={20} />
              Nuevo Socio
            </DialogTitle>
          </DialogHeader>
          {renderMemberForm({ onSubmit: handleCreate, submitLabel: 'Crear Socio' })}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="text-emerald-400" size={20} />
              Editar Socio
            </DialogTitle>
          </DialogHeader>
          {renderMemberForm({ onSubmit: handleUpdate, submitLabel: 'Guardar Cambios' })}
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="text-emerald-400" size={20} />
              Ficha del Socio
            </DialogTitle>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-6">
              {/* Basic Info Card */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    {selectedMember.member_number && (
                      <span className="inline-block text-xs font-mono bg-emerald-900/40 text-emerald-300 px-2 py-0.5 rounded mb-1" data-testid="detail-member-number">
                        {selectedMember.member_number}
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-white">{selectedMember.name}</h3>
                    <div className="mt-1">
                      {getStatusBadge(selectedMember)}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Phone size={14} />
                    <span>{selectedMember.phone}</span>
                  </div>
                  {selectedMember.email && (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Mail size={14} />
                      <span>{selectedMember.email}</span>
                    </div>
                  )}
                  {selectedMember.birth_date && (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Calendar size={14} />
                      <span>{formatDate(selectedMember.birth_date)}</span>
                    </div>
                  )}
                  {selectedMember.postal_code && (
                    <div className="flex items-center gap-2 text-zinc-400 col-span-2">
                      <MapPin size={14} />
                      <span className="font-mono text-zinc-300">{selectedMember.postal_code}</span>
                      {selectedMember.city && (
                        <span className="text-zinc-500">· {selectedMember.city}{selectedMember.province ? `, ${selectedMember.province}` : ''}</span>
                      )}
                    </div>
                  )}
                  {selectedMember.gender && (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <User size={14} />
                      <span>{selectedMember.gender}</span>
                    </div>
                  )}
                  {selectedMember.joined_at && (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Calendar size={14} />
                      <span>Alta: {formatDate(selectedMember.joined_at)}</span>
                    </div>
                  )}
                </div>
                
                {selectedMember.emergency_contact && (
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-1">Contacto de emergencia</p>
                    <p className="text-white">{selectedMember.emergency_contact} - {selectedMember.emergency_phone}</p>
                  </div>
                )}
                
                {selectedMember.notes && (
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-1">Notas</p>
                    <p className="text-zinc-300">{selectedMember.notes}</p>
                  </div>
                )}
              </div>
              
              {/* Stats Cards: Active subscription info + Debt */}
              {(() => {
                const activeSub = (selectedMember.subscriptions || []).find(s => s.is_active);
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-zinc-900 rounded-lg p-3 sm:col-span-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <CreditCard className="text-blue-400" size={18} />
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Suscripción activa</p>
                        </div>
                        {activeSub && (
                          <Badge className="bg-emerald-600/20 text-emerald-400 text-[10px]">Activa</Badge>
                        )}
                      </div>
                      {activeSub ? (
                        <div>
                          <p className="text-lg font-bold text-white" data-testid="active-sub-name">{activeSub.item_name}</p>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <div className="bg-zinc-950 rounded p-2">
                              <p className="text-zinc-500 text-[10px] uppercase">Créditos</p>
                              <div className="flex items-baseline gap-1 mt-0.5">
                                <span className="text-blue-300 font-bold text-base">{activeSub.classes_consumed ?? 0}</span>
                                <span className="text-zinc-500 text-[11px]">/ {activeSub.classes_total ?? '—'}</span>
                                <span className="text-zinc-500 text-[11px] ml-1">usados</span>
                              </div>
                              {activeSub.classes_total != null && (
                                <p className="text-[10px] text-zinc-500 mt-0.5">
                                  Quedan <b className="text-emerald-400">{activeSub.classes_remaining}</b>
                                </p>
                              )}
                              {isAdmin && activeSub.classes_total != null && (
                                <div className="flex gap-1 mt-1.5">
                                  <button
                                    onClick={() => handleConsumeCredit(activeSub, -1)}
                                    disabled={activeSub.classes_consumed <= 0}
                                    className="flex-1 h-6 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Restaurar un crédito"
                                    data-testid="credit-restore-btn"
                                  >−</button>
                                  <button
                                    onClick={() => handleConsumeCredit(activeSub, 1)}
                                    disabled={activeSub.classes_remaining <= 0}
                                    className="flex-1 h-6 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Registrar un uso"
                                    data-testid="credit-consume-btn"
                                  >+</button>
                                </div>
                              )}
                            </div>
                            <div className="bg-zinc-950 rounded p-2">
                              <p className="text-zinc-500 text-[10px] uppercase">Vigencia</p>
                              <div className="flex items-baseline gap-1 mt-0.5">
                                <span className={`font-bold text-base ${activeSub.days_remaining <= 7 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                  {activeSub.days_remaining ?? '—'}
                                </span>
                                <span className="text-zinc-500 text-[11px]">día{activeSub.days_remaining === 1 ? '' : 's'} restantes</span>
                              </div>
                              <p className="text-[10px] text-zinc-500 mt-0.5">
                                Hasta <b className="text-zinc-300">{formatDate(activeSub.subscription_end)}</b>
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-500 py-2">Sin suscripción activa</p>
                      )}
                    </div>
                    <div className="bg-zinc-900 rounded-lg p-3 text-center flex flex-col justify-center">
                      <AlertCircle className={`mx-auto mb-1 ${selectedMember.pending_debt > 0 ? 'text-red-400' : 'text-zinc-500'}`} size={20} />
                      <p className={`text-lg font-bold ${selectedMember.pending_debt > 0 ? 'text-red-400' : 'text-white'}`}>
                        {formatCurrency(selectedMember.pending_debt)}
                      </p>
                      <p className="text-xs text-zinc-500">Deuda pendiente</p>
                    </div>
                  </div>
                );
              })()}
              
              {/* Subscriptions Section */}
              <div>
                <h4 className="text-sm font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                  <CreditCard size={14} className="text-blue-400" />
                  Suscripciones
                  {selectedMember.subscriptions?.length > 0 && (
                    <Badge className="bg-blue-600/20 text-blue-300 ml-1">{selectedMember.subscriptions.length}</Badge>
                  )}
                </h4>
                {selectedMember.subscriptions && selectedMember.subscriptions.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="member-subscriptions-list">
                    {selectedMember.subscriptions.map((sub, idx) => (
                      <div
                        key={`${sub.sale_id}-${sub.item_index}`}
                        className={`rounded p-3 border ${sub.is_active ? 'bg-blue-900/15 border-blue-800/50' : sub.refunded ? 'bg-zinc-900 border-zinc-800 opacity-70' : 'bg-zinc-900 border-zinc-800'}`}
                        data-testid={`subscription-row-${idx}`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-medium truncate">{sub.item_name}</p>
                              {sub.is_active ? (
                                <Badge className="bg-emerald-600/20 text-emerald-400">Activa</Badge>
                              ) : sub.refunded ? (
                                <Badge className="bg-zinc-600/20 text-zinc-400">Devuelta</Badge>
                              ) : (
                                <Badge className="bg-red-600/20 text-red-400">Expirada</Badge>
                              )}
                              {sub.is_custom_end && (
                                <Badge className="bg-purple-600/20 text-purple-300 text-xs">Editada</Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                              <div className="text-zinc-500">
                                Inicio: <span className="text-zinc-300">{formatDate(sub.start_date)}</span>
                              </div>
                              <div className="text-zinc-500">
                                Caduca: <span className={sub.is_active ? 'text-blue-300' : 'text-red-300'}>{formatDate(sub.subscription_end)}</span>
                              </div>
                              <div className="text-zinc-500">
                                Duración: <span className="text-zinc-300">{sub.duration_days} días</span>
                              </div>
                              <div className="text-zinc-500">
                                Importe: <span className="text-emerald-400 font-mono">{formatCurrency(sub.subtotal)}</span>
                              </div>
                            </div>
                          </div>
                          {isAdmin && !sub.refunded && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditSubscriptionModal(sub)}
                              className="border-blue-700 text-blue-300 hover:bg-blue-900/30 shrink-0"
                              data-testid={`edit-subscription-btn-${idx}`}
                            >
                              <Calendar size={12} className="mr-1" />
                              Editar fecha
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500 bg-zinc-900 rounded p-3">Este socio no tiene suscripciones registradas.</p>
                )}
              </div>

              {/* Purchase History (products only) */}
              {selectedMember.purchase_history && selectedMember.purchase_history.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                    <Package size={14} className="text-emerald-400" />
                    Historial de compras
                    <Badge className="bg-emerald-600/20 text-emerald-300 ml-1">{selectedMember.purchase_history.length}</Badge>
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="member-purchase-history">
                    {selectedMember.purchase_history.slice(0, 30).map((sale, idx) => {
                      const productItems = (sale.items || []).filter(i => i.item_type !== 'subscription');
                      return (
                        <div key={sale.id || idx} className="bg-zinc-900 rounded p-2 text-sm">
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-white truncate">
                                {productItems.map(i => `${i.item_name} x${i.quantity}`).join(', ') || 'Venta'}
                              </p>
                              <p className="text-xs text-zinc-500">
                                {formatDate(sale.created_at)} · {sale.payment_method}
                                {sale.refunded && <span className="text-red-400 ml-2">· Devuelta</span>}
                              </p>
                            </div>
                            <span className={`font-mono shrink-0 ${sale.refunded ? 'text-zinc-500 line-through' : 'text-emerald-400'}`}>
                              {formatCurrency(sale.total_final || sale.total)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Debts */}
              {selectedMember.debts && selectedMember.debts.filter(d => d.status !== 'paid').length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                    <DollarSign size={14} />
                    Deudas pendientes
                  </h4>
                  <div className="space-y-2">
                    {selectedMember.debts.filter(d => d.status !== 'paid').map((debt, idx) => (
                      <div key={idx} className="bg-red-900/20 border border-red-800/50 rounded p-2 flex justify-between items-center text-sm">
                        <div>
                          <p className="text-white">{debt.description || 'Deuda'}</p>
                          <p className="text-xs text-zinc-500">{formatDate(debt.created_at)}</p>
                        </div>
                        <span className="font-mono text-red-400">{formatCurrency(debt.remaining_amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            {isAdmin && selectedMember && (
              selectedMember.status !== 'no_socio' ? (
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (window.confirm(`¿Marcar a ${selectedMember.name} como NO SOCIO? Ya no se le podrán vender suscripciones.`)) {
                      await handleDeactivate(selectedMember.id);
                      setShowDetailModal(false);
                    }
                  }}
                  className="border-red-900/60 text-red-400 hover:bg-red-950/30 mr-auto"
                  data-testid="detail-mark-no-socio-btn"
                >
                  <UserX size={14} className="mr-2" />
                  Marcar como NO SOCIO
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={async () => {
                    await handleReactivate(selectedMember.id);
                    setShowDetailModal(false);
                  }}
                  className="border-emerald-900/60 text-emerald-400 hover:bg-emerald-950/30 mr-auto"
                  data-testid="detail-restore-socio-btn"
                >
                  <UserCheck size={14} className="mr-2" />
                  Restaurar como socio
                </Button>
              )
            )}
            <Button variant="outline" onClick={() => setShowDetailModal(false)} className="border-zinc-700">
              Cerrar
            </Button>
            {isAdmin && selectedMember?.status !== 'no_socio' && selectedMember?.email && (
              <Button
                variant="outline"
                onClick={() => handleInvitePortal(selectedMember)}
                className="border-blue-800/60 text-blue-300 hover:bg-blue-950/30"
                data-testid="invite-portal-btn"
              >
                <Send size={14} className="mr-2" />
                Invitar al portal
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => handlePermanentDelete(selectedMember)}
                className="border-red-900/80 text-red-400 hover:bg-red-950/40"
                data-testid="permanent-delete-btn"
              >
                <Trash2 size={14} className="mr-2" />
                Eliminar
              </Button>
            )}
            <Button onClick={() => { setShowDetailModal(false); openEditModal(selectedMember); }} className="bg-emerald-600 hover:bg-emerald-700">
              <Edit size={14} className="mr-2" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Expiry Modal (Admin only) */}
      <Dialog open={showEditSubscriptionModal} onOpenChange={setShowEditSubscriptionModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md" data-testid="edit-subscription-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="text-blue-400" size={20} />
              Editar fecha de caducidad
            </DialogTitle>
          </DialogHeader>
          {selectedSubscription && (
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded p-3 text-sm">
                <p className="text-white font-medium">{selectedSubscription.item_name}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div className="text-zinc-500">
                    Inicio: <span className="text-zinc-300">{formatDate(selectedSubscription.start_date)}</span>
                  </div>
                  <div className="text-zinc-500">
                    Actual: <span className="text-zinc-300">{formatDate(selectedSubscription.subscription_end)}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Nueva fecha de caducidad</Label>
                <Input
                  type="date"
                  value={newExpiryDate}
                  onChange={(e) => setNewExpiryDate(e.target.value)}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="new-expiry-date-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Solo los administradores pueden modificar esta fecha. Útil para añadir días de cortesía o regularizar suscripciones.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowEditSubscriptionModal(false); setSelectedSubscription(null); setNewExpiryDate(''); }}
              className="border-zinc-700"
              data-testid="cancel-edit-subscription-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateSubscriptionEnd}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-edit-subscription-btn"
            >
              Guardar fecha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Members;
